import { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL } from '../founderCall';
import type { FeatureContent } from './types';

export const candidateFeedback: FeatureContent = {
  slug: 'candidate-feedback',
  path: '/features/candidate-feedback',
  scorecardConnector:
    'This closes the loop for everyone a scorecard does not advance: personalised, skill-based feedback, never silence.',
  audience: 'company',
  icon: 'Heart',
  name: 'Personalised Feedback',
  stage: 3,
  navLabel: 'Candidate Feedback',
  navDescription: 'Never ghost a candidate again. Personalised feedback at scale.',
  primaryKeyword: 'candidate experience',
  seo: {
    title: 'Candidate Experience & Feedback at Scale | Talenture.ai',
    description:
      'Candidate feedback at scale: personalised, skill-based notes for everyone who does not progress, drafted from real match analysis. Protect your employer brand.',
    keywords: [
      'candidate experience',
      'candidate feedback',
      'personalised rejection feedback',
      'rejection email software',
      'employer brand',
      'recruitment feedback automation',
    ],
    ogTitle: 'Never Ghost a Candidate Again | Talenture.ai',
    ogDescription:
      'Personalised, skill-based feedback for every candidate who does not progress. Protect your employer brand at scale, without writing each note by hand.',
  },
  hero: {
    eyebrow: 'Stage 3 of the hiring mission · Prepare candidates',
    whereThisFits:
      'Everyone who does not progress leaves with useful feedback, so preparation strengthens your brand, not just your shortlist.',
    h1: 'Never ghost a candidate again',
    subhead:
      'Over half of job seekers report being ghosted by employers (Criteria Corp Candidate Experience Report). Talenture drafts personalised, skill-based feedback for everyone who does not progress, so your employer brand survives every rejection.',
    worksWithAts: true,
    primaryCta: { label: 'See a sample scorecard', href: '/sample-scorecard' },
    secondaryCta: { label: FOUNDER_CALL_LABEL, href: FOUNDER_CALL_URL },
  },
  definition: {
    heading: 'What is personalised candidate feedback?',
    body: 'Personalised candidate feedback is honest, specific guidance sent to candidates who are not moving forward, instead of silence or a form letter. Talenture drafts it from the real match analysis, drawing on the top skill and experience gaps, so every candidate hears back with something useful and your brand stays intact.',
  },
  sections: [
    {
      icon: 'MessageSquareText',
      title: 'Grounded in the real analysis',
      body: 'Feedback is not invented. Talenture builds each note from the candidate\'s actual match results, focusing on the few gaps that mattered most.',
      bullets: [
        'Drawn from the top 3 to 5 gaps in the match analysis',
        'Covers skills, experience, and cultural signals',
        'Specific to the person, not a template',
      ],
    },
    {
      icon: 'Sliders',
      title: 'The right tone for the stage',
      body: 'A candidate who interviewed deserves a warmer message than one screened out early. Talenture adjusts the tone to where the candidate reached in the process.',
      bullets: [
        'Brief or detailed, your choice',
        'Warmer for candidates who interviewed',
        'Honest without being harsh',
      ],
    },
    {
      icon: 'Send',
      title: 'You stay in control',
      body: 'Every message is a draft until you approve it. Review or edit, then send through a branded email template, so nothing goes out that you would not write yourself.',
    },
  ],
  visual: {
    component: 'MockInterviewFeedbackScreen',
    url: 'talenture.ai/mock-interview/senior-platform-engineer/results',
    caption: 'Every note is a draft until you approve it. Nothing reaches a candidate unread.',
  },
  supportingVisuals: [
    {
      component: 'MockInterviewSessionScreen',
      url: 'talenture.ai/mock-interview/senior-platform-engineer',
      heading: 'Where the feedback comes from',
      caption:
        'The practice session itself: questions generated from your job post, answered in the candidate\'s own words.',
    },
  ],
  howTo: {
    heading: 'How personalised candidate feedback works',
    intro: 'Three steps from match analysis to a useful, approved message.',
    steps: [
      {
        title: 'Find the gaps that mattered',
        body: 'Talenture uses the candidate’s actual match analysis to identify the top 3 to 5 gaps across skills, experience, and cultural signals.',
      },
      {
        title: 'Draft feedback for the process stage',
        body: 'The note is written specifically for the person, with a tone and level of detail suited to how far they reached in the hiring process.',
      },
      {
        title: 'Review, edit, and send',
        body: 'The message remains a draft until you approve it, then it is sent through your branded email template.',
      },
    ],
  },
  proofPoints: [
    { value: 'Over half', label: 'of job seekers report being ghosted (Criteria Corp)' },
    { value: '3 to 5', label: 'real gaps behind every note' },
    { value: 'Every', label: 'candidate gets a response' },
  ],
  faq: [
    {
      question: 'Why does candidate feedback matter for hiring?',
      answer:
        'Over half of job seekers report being ghosted by employers (Criteria Corp Candidate Experience Report), and that silence damages your employer brand. Candidates who are treated with respect, even in rejection, are more likely to apply again and to speak well of you. Feedback is a brand investment, not a courtesy.',
    },
    {
      question: 'Is the feedback generic or specific?',
      answer:
        'Specific. Each note is drafted from the candidate\'s real match analysis, focusing on the top 3 to 5 gaps across skills, experience, and cultural signals. It is tailored to the individual rather than a reusable template.',
    },
    {
      question: 'Do recruiters review messages before they send?',
      answer:
        'Yes. Talenture drafts the feedback, but every message stays a draft until a recruiter reviews or edits it and approves the send. You keep full control over what reaches each candidate.',
    },
    {
      question: 'Does the tone change depending on the stage?',
      answer:
        'It does. A candidate who reached an interview receives a warmer, fuller message than one screened out early. The tone is matched to how far the candidate progressed.',
    },
  ],
  relatedSlugs: ['ai-candidate-matching', 'application-tracking', 'match-insights'],
};
